// Per-route SEO controller. Sets <title>, meta description/keywords,
// Open Graph tags, Twitter card, and canonical link on mount, then restores
// previous values on unmount. Pure DOM (no extra deps).

import { useEffect } from "react";

const DEFAULTS = {
  title: "LexWorkspace | Stenography Training & AI Legal Drafting",
  description:
    "LexWorkspace — Stenography speed training (StenoPaceMaster) and AI legal drafting (DraftMaster.ai) for professionals in Washington DC, New York City, Los Angeles, Chicago and Houston.",
  image: "",
  siteName: "LexWorkspace",
};

function upsertMeta(attr, key, value) {
  if (value == null) return null;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  const prior = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return { el, prior, created: !prior };
}

function upsertCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  const prior = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
  return { el, prior };
}

function upsertRobots(value) {
  if (value == null) return null;
  let el = document.querySelector('meta[name="robots"]');
  const prior = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return { el, prior, created: !prior };
}

export default function useSEO({
  title,
  description,
  keywords,
  image,
  url,
  noindex = false,
  type = "website",
} = {}) {
  useEffect(() => {
    const priorTitle = document.title;
    const finalTitle = title || DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const finalImage = image || DEFAULTS.image;

    document.title = finalTitle;

    const tracked = [];
    tracked.push(upsertMeta("name", "description", finalDesc));
    if (keywords) tracked.push(upsertMeta("name", "keywords", keywords));
    tracked.push(upsertMeta("property", "og:title", finalTitle));
    tracked.push(upsertMeta("property", "og:description", finalDesc));
    tracked.push(upsertMeta("property", "og:type", type));
    tracked.push(upsertMeta("property", "og:url", finalUrl));
    tracked.push(upsertMeta("property", "og:site_name", DEFAULTS.siteName));
    if (finalImage) tracked.push(upsertMeta("property", "og:image", finalImage));
    tracked.push(upsertMeta("name", "twitter:card", finalImage ? "summary_large_image" : "summary"));
    tracked.push(upsertMeta("name", "twitter:title", finalTitle));
    tracked.push(upsertMeta("name", "twitter:description", finalDesc));
    if (finalImage) tracked.push(upsertMeta("name", "twitter:image", finalImage));

    const canonical = upsertCanonical(finalUrl);
    const robots = noindex ? upsertRobots("noindex, nofollow") : null;

    return () => {
      document.title = priorTitle;
      tracked.forEach((t) => {
        if (!t || !t.el) return;
        if (t.created) t.el.remove();
        else if (t.prior != null) t.el.setAttribute("content", t.prior);
      });
      if (canonical) {
        if (canonical.prior == null) canonical.el.remove();
        else canonical.el.setAttribute("href", canonical.prior);
      }
      if (robots) {
        if (robots.created) robots.el.remove();
        else robots.el.setAttribute("content", robots.prior);
      }
    };
  }, [title, description, keywords, image, url, noindex, type]);
}
